import { PreviewCustomRule } from '../../models/filter-models';

export type LabelLayoutStyle = 'scattered' | 'stacked';

export type StackedRuleCategory = 'prominent' | 'hidden' | 'excluded';

const STACKED_CONTAINER_HEIGHT_PX = 280;
const STACKED_PADDING_Y_PX = 48 + 40;
const STACKED_PROMINENT_LABEL_HEIGHT_PX = 22;
const STACKED_HIDDEN_LABEL_HEIGHT_PX = 18;
const STACKED_RARE_LABEL_HEIGHT_MULTIPLIER = 1.6;
export const STACKED_DIRECTION_CHUNK_SIZE = 10;

export interface StackedSlot {
  rule: PreviewCustomRule;
  indexInChunk: number;
  lastIndexInChunk: number;
  direction: number;
}

export type StackedSubStack = StackedSlot[];
export type StackedColumnLayout = StackedSubStack[];
export type StackedLayout = StackedColumnLayout[];

export const STACKED_SUB_STACK_GAP_PX = 12;

export interface StackedLayoutOptions {
  containerHeightPx?: number;
  prominentLabelHeightPx?: number;
  hiddenLabelHeightPx?: number;
  forceVisible?: boolean;
  isRuleHidden?: (rule: PreviewCustomRule) => boolean;
}

export function classifyStackedRule(
  rule: PreviewCustomRule,
  options: StackedLayoutOptions,
): StackedRuleCategory {
  const forceVisible = options.forceVisible ?? false;
  const hidden = options.isRuleHidden?.(rule) ?? rule.displayOptions.hidden ?? false;
  const active = rule.displayOptions.active;

  if (!forceVisible && active && hidden) {
    return 'excluded';
  }
  if (active && (forceVisible || !hidden)) {
    return 'prominent';
  }
  return 'hidden';
}

function stackedRuleHeightUnits(rule: PreviewCustomRule, labelHeightPx: number): number {
  return rule.rareName ? labelHeightPx * STACKED_RARE_LABEL_HEIGHT_MULTIPLIER : labelHeightPx;
}

function packRulesIntoColumns(
  rules: PreviewCustomRule[],
  availableHeight: number,
  labelHeightPx: number,
): PreviewCustomRule[][] {
  if (rules.length === 0) return [];

  const columns: PreviewCustomRule[][] = [];
  let currentColumn: PreviewCustomRule[] = [];
  let usedHeight = 0;

  for (const rule of rules) {
    const ruleHeight = stackedRuleHeightUnits(rule, labelHeightPx);

    if (currentColumn.length > 0 && usedHeight + ruleHeight > availableHeight) {
      columns.push(currentColumn);
      currentColumn = [rule];
      usedHeight = ruleHeight;
    } else {
      currentColumn.push(rule);
      usedHeight += ruleHeight;
    }
  }

  if (currentColumn.length > 0) {
    columns.push(currentColumn);
  }

  return columns;
}

function buildColumnSlots(column: PreviewCustomRule[]): StackedSlot[] {
  return column.map((rule, itemIndex) => {
    const chunkIndex = Math.floor(itemIndex / STACKED_DIRECTION_CHUNK_SIZE);
    const chunkStart = chunkIndex * STACKED_DIRECTION_CHUNK_SIZE;
    const chunkEnd = Math.min(chunkStart + STACKED_DIRECTION_CHUNK_SIZE, column.length);
    const chunkSize = chunkEnd - chunkStart;

    return {
      rule,
      indexInChunk: itemIndex - chunkStart,
      lastIndexInChunk: chunkSize - 1,
      direction: chunkIndex % 2 === 0 ? 1 : -1,
    };
  });
}

function groupRulesIntoSubStacks(
  rules: PreviewCustomRule[],
  options: StackedLayoutOptions,
): StackedSubStack[] {
  if (rules.length === 0) return [];

  const containerHeightPx = options.containerHeightPx ?? STACKED_CONTAINER_HEIGHT_PX;
  const prominentLabelHeightPx = options.prominentLabelHeightPx ?? STACKED_PROMINENT_LABEL_HEIGHT_PX;
  const hiddenLabelHeightPx = options.hiddenLabelHeightPx ?? STACKED_HIDDEN_LABEL_HEIGHT_PX;
  const availableHeight = containerHeightPx - STACKED_PADDING_Y_PX;

  const prominentRules: PreviewCustomRule[] = [];
  const hiddenRules: PreviewCustomRule[] = [];

  for (const rule of rules) {
    const category = classifyStackedRule(rule, options);
    if (category === 'prominent') prominentRules.push(rule);
    else if (category === 'hidden') hiddenRules.push(rule);
  }

  const prominentColumns = packRulesIntoColumns(prominentRules, availableHeight, prominentLabelHeightPx);
  const hiddenColumns = packRulesIntoColumns(hiddenRules, availableHeight, hiddenLabelHeightPx);

  return [...prominentColumns, ...hiddenColumns].map(buildColumnSlots);
}

function subStackHeightPx(subStack: StackedSubStack, options: StackedLayoutOptions): number {
  const prominentLabelHeightPx = options.prominentLabelHeightPx ?? STACKED_PROMINENT_LABEL_HEIGHT_PX;
  const hiddenLabelHeightPx = options.hiddenLabelHeightPx ?? STACKED_HIDDEN_LABEL_HEIGHT_PX;

  return subStack.reduce((sum, slot) => {
    const category = classifyStackedRule(slot.rule, options);
    const labelHeightPx = category === 'hidden' ? hiddenLabelHeightPx : prominentLabelHeightPx;
    return sum + stackedRuleHeightUnits(slot.rule, labelHeightPx);
  }, 0);
}

function packSubStacksIntoColumns(
  subStacks: StackedSubStack[],
  options: StackedLayoutOptions,
): StackedColumnLayout[] {
  if (subStacks.length === 0) return [];

  const availableHeight =
    (options.containerHeightPx ?? STACKED_CONTAINER_HEIGHT_PX) - STACKED_PADDING_Y_PX;
  const gap = STACKED_SUB_STACK_GAP_PX;

  const columns: StackedColumnLayout[] = [];
  let currentColumn: StackedColumnLayout = [];
  let usedHeight = 0;

  for (const subStack of subStacks) {
    const stackHeight = subStackHeightPx(subStack, options);
    const needsGap = currentColumn.length > 0;
    const addedHeight = stackHeight + (needsGap ? gap : 0);

    if (currentColumn.length > 0 && usedHeight + addedHeight > availableHeight) {
      columns.push(currentColumn);
      currentColumn = [subStack];
      usedHeight = stackHeight;
    } else {
      currentColumn.push(subStack);
      usedHeight += addedHeight;
    }
  }

  if (currentColumn.length > 0) {
    columns.push(currentColumn);
  }

  return columns;
}

export function groupRulesIntoStackedLayout(
  rules: PreviewCustomRule[],
  options: StackedLayoutOptions = {},
): StackedLayout {
  return groupRulesIntoSubStacks(rules, options).map((subStack) => [subStack]);
}

export function groupRuleGroupsIntoStackedLayout(
  ruleGroups: PreviewCustomRule[][],
  options: StackedLayoutOptions = {},
): StackedLayout {
  const subStacks = ruleGroups
    .flatMap((group) => groupRulesIntoSubStacks(group, options));

  return packSubStacksIntoColumns(subStacks, options);
}
